import { useEffect, useMemo, useRef, useState } from "react";
import { Polyline, CircleMarker, Marker } from "react-leaflet";
import { DataPoint } from "./geolocationApi";
import { LatLng, LeafletMouseEvent } from "leaflet";
import { TooltipVehicleTrip } from "./tooltipVehicleTrip";
import { TooltipTripCard } from "./tooltipTripCard";
import { VehicleGeolocated } from "./index";
import {
  dateFormatterDayMonthYear2Digits,
  dateFormatterHourMinute,
  getCarBearing,
  getJunctionDataPoints,
  getArrowPositions,
} from "@qivia/ui";
import styled from "styled-components";
import { getClosestSegmentIndex } from "./getClosestSegmentIndex";
import L from "leaflet";
import { TripsDetails } from "../fleet/trips/tripsApi";
import startTripIconUrl from "../../../assets/startTripIcon.svg";
import endTripIconUrl from "../../../assets/endTripIcon.svg";
import junctionTripIconUrl from "../../../assets/junctionTripIcon.svg";
import arrowTripIconUrl from "../../../assets/arrowTripIcon.svg";
import carTripIconUrl from "../../../assets/carTripIcon.svg";

export const VehicleTripOnMap = (props: {
  showTripForVehicleUuid: string | null;
  isNotProductionEnvironment: () => boolean;
  fakeVehicleGeolocatedForDemo: VehicleGeolocated[];
  lastDataPointsVehiclesToDisplayed: VehicleGeolocated[] | null;
  isOngoing: boolean;
  tripsDetailsForSelectedVehicle: TripsDetails[];
}) => {
  const {
    showTripForVehicleUuid,
    isNotProductionEnvironment,
    fakeVehicleGeolocatedForDemo,
    lastDataPointsVehiclesToDisplayed,
    isOngoing,
    tripsDetailsForSelectedVehicle,
  } = props;

  const vehicleTripToDisplay = useMemo(() => {
    if (!showTripForVehicleUuid) return null;
    if (isNotProductionEnvironment()) {
      return fakeVehicleGeolocatedForDemo?.find(
        (v) => v.uuid === showTripForVehicleUuid,
      );
    } else {
      return lastDataPointsVehiclesToDisplayed?.find(
        (v) => v.uuid === showTripForVehicleUuid,
      );
    }
  }, [
    fakeVehicleGeolocatedForDemo,
    isNotProductionEnvironment,
    lastDataPointsVehiclesToDisplayed,
    showTripForVehicleUuid,
  ]);

  const polylineRef = useRef<L.Polyline | null>(null);
  const [hoverTripVehicleAndDataPoint, setHoverTripVehicleAndDataPoint] =
    useState<{
      vehicle: VehicleGeolocated;
      dataPoint: DataPoint;
    } | null>(null);
  const [mouseLatLng, setMouseLatLng] = useState<LatLng | null>(null);

  const dataPoints = useMemo(
    () => vehicleTripToDisplay?.dataPoints ?? [],
    [vehicleTripToDisplay?.dataPoints],
  );

  useEffect(() => {
    if (!vehicleTripToDisplay) return;
    const polyline = polylineRef.current;
    if (!polyline) return;
    const map = (polyline as unknown as { _map: L.Map })._map;
    if (!map) return;

    const latlngs = polyline.getLatLngs() as LatLng[];

    const onMove = (e: LeafletMouseEvent) => {
      //on set la souris en focntion de sa position sur la polyline
      setMouseLatLng(e.latlng);

      //on trouve le segment le plus proche de la souris
      const mouse = e.containerPoint;
      const segmentIndex = getClosestSegmentIndex(latlngs, mouse, map);

      setHoverTripVehicleAndDataPoint({
        vehicle: vehicleTripToDisplay,
        dataPoint: dataPoints[segmentIndex],
      });
    };

    const onLeave = () => {
      setMouseLatLng(null);
      setHoverTripVehicleAndDataPoint(null);
    };

    polyline.on("mousemove", onMove);
    polyline.on("mouseout", onLeave);

    return () => {
      polyline.off("mousemove", onMove);
      polyline.off("mouseout", onLeave);
    };
  }, [dataPoints, vehicleTripToDisplay]);

  const firstDataPoint = dataPoints[0];
  const lastDataPoint = dataPoints[dataPoints.length - 1];

  const carBearing = useMemo(() => getCarBearing(dataPoints), [dataPoints]);

  const junctionDataPoints = useMemo(
    () => getJunctionDataPoints(dataPoints, tripsDetailsForSelectedVehicle),
    [dataPoints, tripsDetailsForSelectedVehicle],
  );

  const arrowMarkers = useMemo(
    () =>
      getArrowPositions(dataPoints, tripsDetailsForSelectedVehicle).map(
        (a) => ({ ...a, icon: createArrowIcon(a.bearing) }),
      ),
    [dataPoints, tripsDetailsForSelectedVehicle],
  );

  const currentHoveredTrip = useMemo(
    () =>
      hoverTripVehicleAndDataPoint
        ? (tripsDetailsForSelectedVehicle.find(
            (t) =>
              hoverTripVehicleAndDataPoint.dataPoint.date >= t.startDate &&
              hoverTripVehicleAndDataPoint.dataPoint.date <= t.endDate,
          ) ?? null)
        : null,
    [hoverTripVehicleAndDataPoint, tripsDetailsForSelectedVehicle],
  );

  return (
    <>
      <Polyline
        ref={polylineRef}
        positions={dataPoints.map((p) => [p.lat, p.lon])}
        color="black"
      />

      {firstDataPoint && (
        <Marker
          position={[firstDataPoint.lat, firstDataPoint.lon]}
          icon={startIcon}
          interactive={false}
          zIndexOffset={1000}
        />
      )}

      {lastDataPoint && !isOngoing && (
        <Marker
          position={[lastDataPoint.lat, lastDataPoint.lon]}
          icon={endIcon}
          interactive={false}
          zIndexOffset={1000}
        />
      )}

      {lastDataPoint && isOngoing && (
        <Marker
          position={[lastDataPoint.lat, lastDataPoint.lon]}
          icon={createCarIcon(carBearing)}
          interactive={false}
          zIndexOffset={1000}
        />
      )}

      {junctionDataPoints.map((dp, i) => (
        <Marker
          key={`junction-${i}`}
          position={[dp.lat, dp.lon]}
          icon={junctionIcon}
          interactive={false}
        />
      ))}

      {arrowMarkers.map((arrow, i) => (
        <Marker
          key={`arrow-${i}`}
          position={[arrow.lat, arrow.lon]}
          icon={arrow.icon}
          interactive={false}
        />
      ))}

      {mouseLatLng && hoverTripVehicleAndDataPoint && (
        <CircleMarker
          center={mouseLatLng}
          radius={5}
          color="black"
          fill={true}
          fillColor="black"
          fillOpacity={1}
          interactive={false}
        >
          {currentHoveredTrip ? (
            <TooltipTripCard
              vehicle={hoverTripVehicleAndDataPoint.vehicle}
              trip={currentHoveredTrip}
              dataPointDate={hoverTripVehicleAndDataPoint.dataPoint.date}
            />
          ) : (
            <TooltipVehicleTrip
              vehicle={hoverTripVehicleAndDataPoint.vehicle}
              rightElement={
                <StyledHour>
                  <div>
                    {dateFormatterDayMonthYear2Digits(
                      new Date(hoverTripVehicleAndDataPoint.dataPoint.date),
                    )}
                  </div>
                  {dateFormatterHourMinute(
                    new Date(hoverTripVehicleAndDataPoint.dataPoint.date),
                  ).replaceAll(":", "h")}
                </StyledHour>
              }
              isPermanent={true}
            />
          )}
        </CircleMarker>
      )}
    </>
  );
};

const StyledHour = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const startIcon = L.icon({
  iconUrl: startTripIconUrl,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const endIcon = L.icon({
  iconUrl: endTripIconUrl,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const junctionIcon = L.icon({
  iconUrl: junctionTripIconUrl,
  iconSize: [10, 10],
  iconAnchor: [5, 5],
});

const createArrowIcon = (bearingDeg: number) =>
  L.divIcon({
    className: "",
    html: `<img src="${arrowTripIconUrl}" width="13" height="14" style="transform:rotate(${bearingDeg - 90}deg);transform-origin:5px 6px;display:block" />`,
    iconSize: [13, 14],
    iconAnchor: [5, 6],
  });

const createCarIcon = (bearingDeg: number) =>
  L.divIcon({
    className: "",
    html: `<img src="${carTripIconUrl}" width="43" height="50" style="transform:rotate(${bearingDeg}deg);transform-origin:center;display:block" />`,
    iconSize: [43, 50],
    iconAnchor: [21, 25],
  });
